import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
	process.env.REACT_APP_SUPABASE_URL,
	process.env.REACT_APP_SUPABASE_ANON_KEY
);

const CACHE_TTL = 600000; // 10 minutes

export async function fetchInfoSingleData() {
	const cached = localStorage.getItem('infosingle-data_cache');
	if (cached) {
		const { data, timestamp } = JSON.parse(cached);
		if (Date.now() - timestamp < CACHE_TTL) {
			return data;
		}
	}

	const { data: rawData } = await supabase.from('infosingle-data').select('*');

	if (!rawData) return [];

	const finalOutput = rawData.map(event => ({
		id: 				event.id,
		title: 				event.title || '',
		description_brief: 	event.description_brief || '',
		thumbnail_src: 		event.thumbnail_src || null,
		banner_src: 		event.banner_src || null,
		page_address: 		event.page_address || null,
		date: 				new Date(event.date_to_show).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric'}),
		category: 			event.category || '',
		is_highlight: 		event.highlight || false,
		is_feature: 		event.is_feature || false
	}));

	localStorage.setItem('infosingle-data_cache', JSON.stringify({
		timestamp: Date.now(),
		data: finalOutput
	}));

	return finalOutput;
}

export async function fetchNoticeData(id) {
    const cacheKey = `notice-data_cache_${id}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
            return data;
        }
    }

    const { data: rawData, error } = await supabase
        .from('infosingle-data')
        .select(`
            id, 
            title, 
            description_brief, 
            thumbnail_src, 
            banner_src, 
            date_to_show,       
            category, 
            "page-details"(*)
		`).eq("id", id).single();

    if (error || !rawData) {
        console.error("Supabase Error:", error);
        return null;
    }

    const finalOutput = {
        id:                 rawData.id,
        title:              rawData.title || '',
        description_brief:  rawData.description_brief || '',
        thumbnail_src:      rawData.thumbnail_src || null,
        banner_src:         rawData.banner_src || null,
        category:           rawData.category || '',
        date: new Date(rawData.date_to_show).toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric'
        }),
        body_text:          rawData['page-details']?.body_text || '',
        button_text:        rawData['page-details']?.button_text || null,
        button_link:        rawData['page-details']?.button_link || null
    };

    localStorage.setItem(cacheKey, JSON.stringify({
        data: finalOutput,
        timestamp: Date.now()
    }));
    return finalOutput;
}

export async function fetchTournamentRegisteredData() {
    const { data: rawData, error } = await supabase
        .from('tournament-registered')
        .select(`
            uid,
            "user-data"(uid, first_name, middle_name, last_name, club_name)
		`);

    if (error || !rawData) {
        console.error("Supabase Error:", error);
        return null;
    }

    const finalOutput = rawData.map(user => ({
        uid:            user.uid,
        first_name:     user['user-data']?.first_name || '',
        middle_name:    user['user-data']?.middle_name || '',
        last_name:      user['user-data']?.last_name || '',
        club_name:      user['user-data']?.club_name || ''
    }));

    localStorage.setItem('tournament-registered_cache', JSON.stringify({
        data: finalOutput,
        timestamp: Date.now()
    }));

    return finalOutput;
}

export async function fetchLeagueLeaderboardData(seasons){
    const cacheKey = `league-leaderboard_cache_${seasons.sort().join(',')}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
            return data;
        }
    }

    const {data: qualifierData, error: qualifierError} = await supabase
    .from("league-season-data")
    .select(`
        season_number, qualified, pid, points, point_penalty, net_points, games_played,
        "user-profile"(id, first_name, middle_name, last_name)
    `)
    .in("season_number", seasons);

    const {data: finalsData, error: finalsError} = await supabase
    .from("league-season-finals-data")
    .select(`
        season_number, pid, points, point_penalty, net_points,
        "user-profile"(id, first_name, middle_name, last_name)
    `)
    .in("season_number", seasons);

    if (qualifierError) console.error("Supabase qualifier error:", qualifierError);
    if (finalsError) console.error("Supabase finals error:", finalsError);

    const result = {};
    for (const s of seasons) {
        const qualifiers = (qualifierData || []).filter(row => Number(row.season_number) === s);
        const finals     = (finalsData || []).filter(row => Number(row.season_number) === s)
            .sort((a, b) => b.net_points - a.net_points);
        result[String(s)] = {
            qualifier: qualifiers.length > 0 ? qualifiers : null,
            finals:    finals.length > 0 ? finals : null,
        };
    }

    localStorage.setItem(cacheKey, JSON.stringify({
        data: result,
        timestamp: Date.now()
    }));

    return result;
}